/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 🏗️ PROJECT DATA STRUCTURE - Single Source of Truth for FLAT vs NESTED
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * ⚠️ CRITICAL DATA STRUCTURE CONVENTIONS (Sprint 0 - P0)
 *
 * ┌─────────────────────────────────────────────────────────────────────────────┐
 * │ REDUX (Frontend): ALWAYS FLAT                                              │
 * │   projectInfo.data.trafficLoad, projectInfo.data.surface, etc.             │
 * │   ❌ NEVER nested under method key (asphalt, airport, default)             │
 * │                                                                             │
 * │ BACKEND (API): ALWAYS NESTED                                               │
 * │   projectInfo.data.{method}.trafficLoad, etc.                              │
 * │   Method key: 'asphalt' | 'airport' | 'default' | '' (geogrid)             │
 * └─────────────────────────────────────────────────────────────────────────────┘
 *
 * TRANSFORMATION POINTS (Only 2 - no shortcuts!):
 *   📥 Load: transformProjectFromBackend() → ensureFlatStructure()
 *   📤 Save: BackendPayloadService.prepareProjectPayload() → ensureNestedStructure()
 *
 * TYPE SAFETY USAGE:
 *   - isNestedStructure(data, method) → Detect format before processing
 *   - ensureFlatStructure(data, method) → Guarantee FLAT output for Redux
 *   - ensureNestedStructure(data, method) → Guarantee NESTED output for API
 *   - validateReduxStructure(data, method) → Dev-mode assertion (throws if NESTED)
 *
 * SEE ALSO:
 *   - CLAUDE.md PD3/PD4 for full data convention rules
 *   - ProjectApiService.ts:transformProjectFromBackend() for load flow
 *   - BackendPayloadService.ts:prepareProjectPayload() for save flow
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Common property keys that exist at root level regardless of FLAT/NESTED structure
 * Used for preservation during transformations and validations
 * NOTE: geoGrid is NOT common - it's method-specific data nested under method key (e.g., default.geoGrid for Railway)
 */
export const COMMON_PROJECT_PROPERTIES = ['type', 'notes', 'designerFirmCity', 'nrProject', 'unit', 'method'] as const;

/**
 * Common properties present at root level regardless of structure
 */
export interface CommonProjectProperties {
    type?: string; // Optional - may not be present in all project types (e.g., Geogrid)
    notes?: string;
    designerFirmCity?: string;
    nrProject?: string;
    geoGrid?: any;
    unit?: 'Metric' | 'Imperial';
    method?: string;
}

/**
 * NESTED structure - How backend stores project data
 * Each calculation method (asphalt, airport, unpaved, etc.) has its own nested object
 *
 * Example:
 * {
 *   type: 'paved',
 *   method: 'asphalt',
 *   asphalt: {
 *     roadInfo: { roadType: 'highway', ... },
 *     trafficLoad: { esal: 1000000, ... }
 *   }
 * }
 */
export type NestedProjectData = CommonProjectProperties & {
    // Method-specific nested data
    asphalt?: Record<string, any>;
    airport?: Record<string, any>;
    unpaved?: Record<string, any>;
    // Railway uses 'default' method name (like 'asphalt', 'airport', etc.)
    default?: Record<string, any>;
    // Add other methods as needed
    [key: string]: any;
};

/**
 * FLAT structure - How frontend components expect project data
 * All fields are at root level, method is just a property
 *
 * Example:
 * {
 *   type: 'paved',
 *   method: 'asphalt',
 *   roadInfo: { roadType: 'highway', ... },
 *   trafficLoad: { esal: 1000000, ... }
 * }
 */
export type FlatProjectData = CommonProjectProperties & {
    // All method-specific fields are at root level
    [key: string]: any;
};

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard to check if data is in NESTED format
 *
 * Detection logic:
 * 1. Geogrid projects: No method or empty method → already FLAT
 * 2. Standard projects (including Railway): Check if data has [method] key with object value → NESTED
 *
 * @param data - Project data to check
 * @param method - Calculation method (asphalt, airport, default, etc.)
 * @param projectType - Project type (paved, railways, etc.)
 * @returns true if data is in NESTED format, false if FLAT
 *
 * @example
 * const data = { asphalt: { roadInfo: {...} } };
 * if (isNestedStructure(data, 'asphalt', 'paved')) {
 *   // Handle nested structure
 * }
 */
/**
 * Known method keys that indicate NESTED structure
 * Used as fallback when `method` parameter is missing or unreliable
 */
const KNOWN_NESTED_KEYS = ['asphalt', 'airport', 'default', 'giroud', 'leng', 'bcr', 'static'] as const;

export const isNestedStructure = (data: any, method?: string, projectType?: string): data is NestedProjectData => {
    if (!data || typeof data !== 'object') {
        return false;
    }

    // 🔧 REMOVED Railway .default exception (v5.1.0+)
    // Railway now uses standard FLAT structure like all other projects

    // If method is provided and valid, use standard detection
    if (method && method !== '') {
        const hasMethodKey = data.hasOwnProperty(method) && typeof data[method] === 'object' && data[method] !== null;
        if (hasMethodKey) return true;
    }

    // FALLBACK: If method is missing/empty/unreliable, check for known NESTED keys
    // This catches cases where backend returns NESTED data but method field is corrupted/missing
    for (const key of KNOWN_NESTED_KEYS) {
        if (data.hasOwnProperty(key) && typeof data[key] === 'object' && data[key] !== null) {
            // Verify it's actually method-specific data (has fields like roadInfo, trafficLoad, etc.)
            // Exclude false positives from common properties
            const nestedObj = data[key];
            const hasMethodSpecificData = Object.keys(nestedObj).some(k => !COMMON_PROJECT_PROPERTIES.includes(k as any));
            if (hasMethodSpecificData) {
                console.warn(`[isNestedStructure] Detected NESTED key '${key}' without matching method. Method was: '${method}'. ProjectType was: '${projectType}'`);
                return true;
            }
        }
    }

    // Geogrid standalone or truly flat data
    return false;
};

/**
 * Type guard to check if data is in FLAT format
 *
 * @param data - Project data to check
 * @param method - Calculation method (asphalt, airport, etc.)
 * @param projectType - Project type (paved, railways, etc.)
 * @returns true if data is in FLAT format, false if NESTED
 */
export const isFlatStructure = (data: any, method?: string, projectType?: string): data is FlatProjectData => {
    return !isNestedStructure(data, method, projectType);
};

// ============================================================================
// SAFE ACCESSORS & TRANSFORMERS
// ============================================================================

/**
 * Ensures data is in FLAT format, transforming from NESTED if necessary
 *
 * This is a SAFE ACCESSOR - it guarantees FLAT output regardless of input format.
 * Use this when you need to ensure data is ready for Redux or component consumption.
 *
 * @param data - Project data in either FLAT or NESTED format
 * @param method - Calculation method (asphalt, airport, etc.)
 * @param projectType - Project type (paved, railways, etc.)
 * @returns Data guaranteed to be in FLAT format
 *
 * @example
 * // From backend (might be nested)
 * const backendData = { asphalt: { roadInfo: {...}, trafficLoad: {...} } };
 * const flatData = ensureFlatStructure(backendData, 'asphalt', 'paved');
 * // flatData = { roadInfo: {...}, trafficLoad: {...}, method: 'asphalt' }
 *
 * @example
 * // Already flat (no transformation needed)
 * const formData = { roadInfo: {...}, trafficLoad: {...} };
 * const flatData = ensureFlatStructure(formData, 'asphalt', 'paved');
 * // flatData = { roadInfo: {...}, trafficLoad: {...} } (unchanged)
 */
export const ensureFlatStructure = (data: any, method?: string, projectType?: string): FlatProjectData => {
    if (!data) {
        return data;
    }

    // Already flat - return as-is
    if (isFlatStructure(data, method, projectType)) {
        return data;
    }

    // Data is NESTED - determine which key to flatten
    let keyToFlatten: string | undefined = undefined;

    // First, try using the provided method if it exists in data
    if (method && method !== '' && data.hasOwnProperty(method) && typeof data[method] === 'object') {
        keyToFlatten = method;
    }

    // If method is missing/empty OR method key doesn't exist in data, use fallback detection
    if (!keyToFlatten) {
        for (const key of KNOWN_NESTED_KEYS) {
            if (data.hasOwnProperty(key) && typeof data[key] === 'object' && data[key] !== null) {
                const nestedObj = data[key];
                const hasMethodSpecificData = Object.keys(nestedObj).some(k => !COMMON_PROJECT_PROPERTIES.includes(k as any));
                if (hasMethodSpecificData) {
                    console.warn(`[ensureFlatStructure] Flattening detected NESTED key '${key}' (method was: '${method}')`);
                    keyToFlatten = key;
                    break;
                }
            }
        }

        // If still no key found, return as-is (truly flat or unknown structure)
        if (!keyToFlatten) {
            console.warn('[ProjectDataStructure] Nested structure without method or known key - returning as-is');
            return data;
        }
    }

    // Flatten the nested structure
    // Note: keyToFlatten is guaranteed to be defined here (either from method or detected key)
    const methodData = data[keyToFlatten!];

    // Extract common properties from root
    const commonData: Record<string, any> = {};
    COMMON_PROJECT_PROPERTIES.forEach(prop => {
        if (data.hasOwnProperty(prop)) {
            commonData[prop] = data[prop];
        }
    });

    // Merge: common properties + flattened method data
    return {
        ...commonData,
        ...methodData,
        method: keyToFlatten, // Ensure method is always present (use detected key if method was empty)
    };
};

/**
 * Creates NESTED structure from FLAT data (for backend API calls)
 *
 * This is the INVERSE of ensureFlatStructure().
 * Use this when preparing data for backend API calls.
 *
 * NOTE: BackendPayloadService.preparePayload() already handles this transformation.
 * This utility is provided for completeness and future use cases.
 *
 * @param data - Project data in FLAT format
 * @param method - Calculation method (asphalt, airport, etc.)
 * @param projectType - Project type (paved, railways, etc.)
 * @returns Data in NESTED format ready for backend API
 *
 * @example
 * const flatData = { roadInfo: {...}, trafficLoad: {...}, method: 'asphalt' };
 * const nestedData = ensureNestedStructure(flatData, 'asphalt', 'paved');
 * // nestedData = { asphalt: { roadInfo: {...}, trafficLoad: {...} }, method: 'asphalt' }
 */
export const ensureNestedStructure = (data: any, method?: string, projectType?: string): NestedProjectData => {
    if (!data) {
        return data;
    }

    // Already nested - return as-is
    if (isNestedStructure(data, method, projectType)) {
        return data;
    }

    // Geogrid case - should remain flat (no method key)
    if (!method || method === '') {
        return data;
    }

    // Data is flat - nest it under method key
    const commonData: Record<string, any> = {};
    const methodData: Record<string, any> = {};

    Object.keys(data).forEach(key => {
        if (COMMON_PROJECT_PROPERTIES.includes(key as any)) {
            commonData[key] = data[key];
        } else {
            methodData[key] = data[key];
        }
    });

    return {
        ...commonData,
        [method]: methodData,
    };
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validates that Redux state has FLAT structure (safety check)
 *
 * Use this in development to catch bugs where NESTED data leaks into Redux.
 * In production, this becomes a no-op for performance.
 *
 * @param data - Project data from Redux
 * @param method - Expected calculation method
 * @param componentName - Name of component for logging
 * @throws Error in development if data is NESTED (should never happen)
 */
export const validateReduxStructure = (data: any, method?: string, componentName = 'Unknown'): void => {
    if (process.env.NODE_ENV === 'production') {
        return; // Skip validation in production for performance
    }

    if (isNestedStructure(data, method)) {
        const errorMessage = `[${componentName}] CRITICAL BUG: Redux contains NESTED structure! Redux MUST store FLAT format only. Method: ${method}`;
        console.error(errorMessage, {
            data,
            method,
            hint: 'Check useProjectSubmission.ts - it may be updating Redux with nested backend response',
        });

        // In development, throw error to catch this bug immediately
        if (process.env.NODE_ENV === 'development') {
            throw new Error(errorMessage);
        }
    }
};

/**
 * Development-only helper to log structure type
 *
 * Use this during debugging to understand what format your data is in.
 *
 * @param data - Project data to analyze
 * @param method - Calculation method
 * @param projectType - Project type
 * @param label - Custom label for logging
 */
export const debugStructureType = (data: any, method?: string, projectType?: string, label = 'Data'): void => {
    if (process.env.NODE_ENV !== 'development') {
        return;
    }

    const structureType = isNestedStructure(data, method, projectType) ? 'NESTED' : 'FLAT';
    const keys = data ? Object.keys(data).slice(0, 10) : [];

    console.log(`[ProjectDataStructure] ${label}:`, {
        structureType,
        method,
        projectType,
        topLevelKeys: keys,
        hasMethodKey: method && data ? data.hasOwnProperty(method) : false,
    });
};
